import ArrowForward from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDialog } from 'library/components/controls/dialogs/BaseDialog';
import CustomSeverityDialog from 'library/components/controls/dialogs/CustomSeverityDialog';

import { SearchServiceContractDialog } from './SearchServiceContractDialog';
import { ServiceContract } from './types';

type AddingServiceContractDialogProps = {
	onClose: () => void;
	onConfirm: (contract?: ServiceContract) => void;
	canConnect: boolean;
	canAddManually: boolean;
};

type ActionOptionProps = {
	title: string;
	onClick: () => void;
};

const ActionOption = ({ title, onClick }: ActionOptionProps) => (
	<ButtonBase
		onClick={onClick}
		sx={{
			width: '100%',
			justifyContent: 'space-between',
			textAlign: 'left',
			borderRadius: 1,
			p: 2,
			backgroundColor: ({ palette }) => palette.grey[100],
			'&:hover': {
				backgroundColor: ({ palette }) => palette.grey[200],
			},
		}}>
		<Typography variant="subtitle1">{title}</Typography>
		<ArrowForward fontSize="small" />
	</ButtonBase>
);

export const AddingServiceContractDialog = ({
	onClose,
	onConfirm,
	canConnect,
	canAddManually,
}: AddingServiceContractDialogProps) => {
	const { t } = useTranslation(['serviceContracts', 'commonButton']);
	const [isConnectingExisting, setIsConnectingExisting] = useState(false);
	const [isCanceling, setIsCanceling] = useState(false);

	const handleConnectExisting = useCallback(() => {
		setIsConnectingExisting(true);
	}, []);

	const handleAddManually = useCallback(() => {
		onConfirm();
		onClose();
	}, [onConfirm, onClose]);

	const handleOnCancel = useCallback(() => {
		setIsCanceling(true);
	}, []);

	const handleOnDecline = useCallback(() => {
		setIsCanceling(false);
	}, []);

	const handleOnAccept = useCallback(() => {
		setIsCanceling(false);
		onClose();
	}, [onClose]);

	if (isConnectingExisting) {
		return (
			<SearchServiceContractDialog
				onClose={onClose}
				onConfirm={onConfirm}
			/>
		);
	}

	return (
		<>
			<BaseDialog
				title={t('serviceContracts:select-action')}
				onClose={onClose}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						mt: 1,
					}}>
					{canConnect && (
						<ActionOption
							title={t(
								'serviceContracts:connect-existing-service-contract'
							)}
							onClick={handleConnectExisting}
						/>
					)}
					{canAddManually && (
						<ActionOption
							title={t(
								'serviceContracts:add-new-service-contract-manually'
							)}
							onClick={handleAddManually}
						/>
					)}
				</Box>
				<Divider sx={{ mt: 4, mx: -3 }} />
				<Box
					sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
					<Button
						variant="text"
						color="secondary"
						onClick={handleOnCancel}>
						{t('commonButton:cancel')}
					</Button>
				</Box>
			</BaseDialog>
			<CustomSeverityDialog
				headerText={t('serviceContracts:adding-cancel-dialog-title')}
				content={t('serviceContracts:adding-cancel-dialog-description')}
				onDecline={handleOnDecline}
				onAccept={handleOnAccept}
				acceptButtonLabel={t('commonButton:yes')}
				declineButtonLabel={t('commonButton:no')}
				isOpen={isCanceling}
			/>
		</>
	);
};
